// Editor compartido de GADGETS persistentes (Pantallas: override por device ·
// Grupos: default heredable). Clima/UV viven en el header; los chips (saludo,
// cuenta regresiva, texto del local) en la capa sobre el contenido.
import { useQuery } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { Switch, fieldCls } from '@/features/pos/shared';
import { sigList } from './shared';
import type { GadgetItem, GadgetsCfg } from './player';

const POS = [
  { v: 'sup-centro', l: 'Arriba centro' },
  { v: 'inf-izq', l: 'Abajo izquierda' },
  { v: 'inf-der', l: 'Abajo derecha' },
] as const;
const TIPOS = [
  { v: 'saludo', l: 'Saludo de campaña' },
  { v: 'cuenta', l: 'Cuenta regresiva' },
  { v: 'local', l: 'Texto del local' },
] as const;
const MAX_CHIPS = 3;   // editorial: es cartelería, no un dashboard

export function GadgetsEditor({ value, onChange }: { value: GadgetsCfg; onChange: (v: GadgetsCfg) => void }) {
  const { data: combos = [] } = useQuery<{ id: string; nombre: string; fecha_fin: string | null }[]>({
    queryKey: ['sig-combos-ref'],
    queryFn: () => sigList('combo?select=id,nombre,fecha_fin&deleted_at=is.null&es_activo=is.true&order=nombre'),
  });
  const items = value.items ?? [];
  const setItem = (i: number, patch: Partial<GadgetItem>) =>
    onChange({ ...value, items: items.map((it, j) => (j === i ? { ...it, ...patch } : it)) });
  const ocultarEn = value.ocultar_en ?? ['video', 'anuncio'];
  const toggleOcultar = (kind: string, on: boolean) =>
    onChange({ ...value, ocultar_en: on ? [...ocultarEn.filter((k) => k !== kind), kind] : ocultarEn.filter((k) => k !== kind) });

  return (
    <div className="space-y-2 text-sm">
      <div className="flex flex-wrap gap-4">
        <Switch checked={value.clima ?? false} onChange={(v) => onChange({ ...value, clima: v })} label="☀ Clima en el header (temperatura + icono)" />
        <Switch checked={value.uv ?? false} onChange={(v) => onChange({ ...value, uv: v })} label="Píldora UV (solo UV≥6, 10:00-16:00)" />
        <Switch checked={value.fecha ?? false} onChange={(v) => onChange({ ...value, fecha: v })} label="Fecha junto al reloj" />
      </div>

      <label className="flex items-center gap-2">
        <span className="text-muted-foreground">Velocidad de la cinta inferior (productos):</span>
        <select className={`${fieldCls} !h-8 !w-auto`} value={value.ticker ?? 'normal'}
          onChange={(e) => onChange({ ...value, ticker: e.target.value as GadgetsCfg['ticker'] })}>
          <option value="lenta">Lenta (cómoda de leer)</option>
          <option value="normal">Normal</option>
          <option value="rapida">Rápida</option>
        </select>
        <span className="text-xs text-muted-foreground">El ritmo ya es constante sin importar cuántos productos haya.</span>
      </label>

      <div className="space-y-1.5">
        {items.map((it, i) => (
          <div key={i} className="border rounded-md p-2 space-y-1.5 bg-muted/20">
            <div className="flex items-center gap-2">
              <select className={`${fieldCls} !h-8 !w-44`} value={it.tipo} onChange={(e) => setItem(i, { tipo: e.target.value as GadgetItem['tipo'] })}>
                {TIPOS.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
              </select>
              <select className={`${fieldCls} !h-8 !w-40`} value={it.pos} onChange={(e) => setItem(i, { pos: e.target.value as GadgetItem['pos'] })}>
                {POS.map((p) => <option key={p.v} value={p.v}>{p.l}</option>)}
              </select>
              <button type="button" className="ml-auto inline-flex w-7 h-7 items-center justify-center rounded hover:bg-muted" title="Quitar"
                onClick={() => onChange({ ...value, items: items.filter((_, j) => j !== i) })}>
                <Trash2 className="w-4 h-4 text-red-500" />
              </button>
            </div>
            {it.tipo === 'local' && (
              <input className={`${fieldCls} !h-8`} placeholder="Texto (vacío = nombre del local; ej. Abierto hasta las 22:00)"
                value={it.texto ?? ''} onChange={(e) => setItem(i, { texto: e.target.value || null })} />
            )}
            {it.tipo === 'cuenta' && (
              <div className="flex items-center gap-2">
                <select className={`${fieldCls} !h-8`} value={it.combo_id ?? ''}
                  onChange={(e) => setItem(i, e.target.value ? { combo_id: e.target.value, fecha: null } : { combo_id: null })}>
                  <option value="">Fecha manual…</option>
                  {combos.map((c) => <option key={c.id} value={c.id}>Combo: {c.nombre}{c.fecha_fin ? ` (hasta ${c.fecha_fin})` : ''}</option>)}
                  {it.combo_id && !combos.some((c) => c.id === it.combo_id) && (
                    <option value={it.combo_id}>⚠ Combo eliminado/inactivo — el chip no se mostrará</option>
                  )}
                </select>
                {!it.combo_id && (
                  <input className={`${fieldCls} !h-8 !w-52`} type="datetime-local" value={(it.fecha ?? '').slice(0, 16)}
                    onChange={(e) => setItem(i, { fecha: e.target.value || null })} />
                )}
                <input className={`${fieldCls} !h-8 !w-36`} placeholder="Rótulo" value={it.titulo ?? ''}
                  onChange={(e) => setItem(i, { titulo: e.target.value || null })} />
              </div>
            )}
            {it.tipo === 'saludo' && <p className="text-xs text-muted-foreground">Muestra el saludo de la campaña estacional vigente (si hay). Sin campaña, el chip descansa.</p>}
          </div>
        ))}
        {items.length < MAX_CHIPS && (
          <button type="button" className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
            onClick={() => onChange({ ...value, items: [...items, { tipo: 'local', pos: 'inf-izq' }] })}>
            <Plus className="w-3.5 h-3.5" /> Agregar chip (máx. {MAX_CHIPS})
          </button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
        <span>Ocultar chips en:</span>
        <Switch checked={ocultarEn.includes('video')} onChange={(v) => toggleOcultar('video', v)} label="Video" />
        <Switch checked={ocultarEn.includes('anuncio')} onChange={(v) => toggleOcultar('anuncio', v)} label="Publicidad" />
        <Switch checked={value.ocultar_patrocinado ?? false} onChange={(v) => onChange({ ...value, ocultar_patrocinado: v })} label="Franja vendida" />
      </div>
    </div>
  );
}

export const GADGETS_DEFAULT: GadgetsCfg = { activo: true, clima: true, uv: true, items: [], ocultar_en: ['video', 'anuncio'] };
